import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import FooterSection from "../components/FooterSection";
import DashboardHeader from "../components/DashboardHeader";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.NODE_ENV === "production"
  ? (process.env.REACT_APP_PROD_API_BASE_URL || "https://test.conversationaily.ai")
  : (process.env.REACT_APP_DEV_API_BASE_URL || "https://testone.conversationaily.ai");



const ReportDashboard = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // =========================================================
  // GET CONSULTATION HISTORY
  // =========================================================
  useEffect(() => {
    const fetchConsultationHistory = async () => {
      try {
        setLoading(true);
        setError("");

        // Get token from your existing authentication storage
        const token =
          localStorage.getItem("accessToken") ||
          localStorage.getItem("token");

        if (!token) {
          setError("Authentication token not found.");
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/user/consultation/history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Consultation History API Response:", response.data);

        /*
          Depending on your API response structure, history can be:

          response.data
          response.data.data
          response.data.history
          response.data.data.history

          The below logic handles these common structures.
        */

        const responseData = response?.data;

        const apiHistory =
          responseData?.data?.history ||
          responseData?.history ||
          responseData?.data ||
          responseData ||
          [];

        setHistory(
          Array.isArray(apiHistory)
            ? apiHistory
            : []
        );

      } catch (err) {
        console.error(
          "Failed to fetch consultation history:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Unable to load consultation history."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchConsultationHistory();
  }, []);

  // =========================================================
  // NORMALIZE API DATA
  // =========================================================
  const normalizedHistory = useMemo(() => {
    return history.map((item, index) => {
      return {
        id:
          item?.id ||
          item?.consultation_id ||
          item?.consultationId ||
          index,

        date:
          item?.date ||
          item?.created_at ||
          item?.createdAt ||
          item?.consultation_date ||
          "-",

        case:
          item?.case ||
          item?.case_name ||
          item?.caseName ||
          item?.case_type ||
          item?.caseType ||
          item?.title ||
          item?.scenario ||
          "Consultation",

        duration:
          item?.duration ||
          item?.consultation_duration ||
          "-",

        empathy:
          item?.empathy_score ??
          item?.empathyScore ??
          item?.empathy ??
          item?.score ??
          null,

        status:
          item?.status ||
          "completed",
      };
    });
  }, [history]);

  // =========================================================
  // SORT BY DATE
  // =========================================================
  const sortedHistory = useMemo(() => {
    return [...normalizedHistory].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      if (Number.isNaN(dateA)) return 1;
      if (Number.isNaN(dateB)) return -1;

      return dateB - dateA;
    });
  }, [normalizedHistory]);

  // =========================================================
  // LATEST EMPATHY
  // =========================================================
  const latestEmpathy = useMemo(() => {
    const value = sortedHistory.find(
      (item) =>
        item.empathy !== null &&
        item.empathy !== undefined &&
        item.empathy !== ""
    )?.empathy;

    return value !== undefined && value !== null
      ? Number(value).toFixed(1)
      : "-";
  }, [sortedHistory]);

  // =========================================================
  // CHART DATA
  // oldest -> newest
  // =========================================================
  const empathyData = useMemo(() => {
    return [...sortedHistory]
      .reverse()
      .filter(
        (item) =>
          item.empathy !== null &&
          item.empathy !== undefined &&
          !Number.isNaN(Number(item.empathy))
      )
      .slice(-8)
      .map((item, index) => ({
        session: `S${index + 1}`,
        score: Number(item.empathy),
      }));
  }, [sortedHistory]);

  // =========================================================
  // TREND
  // =========================================================
  const trend = useMemo(() => {
    if (empathyData.length < 2) {
      return {
        arrow: "→",
        text: "not enough data",
      };
    }

    const recent = empathyData.slice(-5);

    if (recent.length < 2) {
      return {
        arrow: "→",
        text: "stable",
      };
    }

    const first = recent[0].score;
    const last = recent[recent.length - 1].score;

    if (last > first) {
      return {
        arrow: "↑",
        text: "rising over last 5",
      };
    }

    if (last < first) {
      return {
        arrow: "↓",
        text: "falling over last 5",
      };
    }

    return {
      arrow: "→",
      text: "stable over last 5",
    };
  }, [empathyData]);

  // =========================================================
  // START CASE
  // =========================================================
  const handleStartCase = (caseItem) => {
    console.log("Start case:", caseItem);
    navigate("/cases")

    // If you already have navigate:
    //
    // navigate("/consultation", {
    //   state: {
    //     case: caseItem
    //   }
    // });
  };

  // =========================================================
  // LOADING
  // =========================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">

        <div className="z-10">
          <Navbar />
        </div>

        <main className="flex-1 flex items-center justify-center px-4">

          <div className="bg-white rounded-3xl shadow-md p-10 text-center">

            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#1D3A73] rounded-full animate-spin mx-auto" />

            <p className="mt-4 text-sm text-gray-500">
              Loading consultation history...
            </p>

          </div>

        </main>

        <FooterSection />

      </div>
    );
  }

  // =========================================================
  // MAIN UI
  // =========================================================
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* =====================================================
          NAVBAR
      ====================================================== */}
      <div className="z-10">
        <Navbar />
      </div>

      {/* =====================================================
          MAIN
      ====================================================== */}
      {/* =====================================================
    MAIN
====================================================== */}
      <main className="flex-1">

        {/* =================================================
      DASHBOARD HEADER AREA
      Gray/white background only
  ================================================== */}
        <div className="bg-gray-50">

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

            {/* HEADER */}
            <DashboardHeader
              lastConsultation={
                sortedHistory.length > 0
                  ? sortedHistory[0].date
                  : null
              }
            />

            {/* ERROR */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl px-5 py-4 mt-5 text-sm">
                {error}
              </div>
            )}

          </div>

        </div>


        {/* =================================================
      BLUE BACKGROUND
      Full width from left to right
  ================================================== */}
        <div className="bg-[#1D3A73] py-8">

          {/* =================================================
        CONTENT WIDTH
    ================================================== */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


            {/* =================================================
          SUMMARY CARDS
      ================================================== */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">

              {/* Consultations */}
              <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">

                <p className="text-xs font-medium text-gray-500">
                  Consultations
                </p>

                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {sortedHistory.length}
                </p>

                <p className="text-[11px] text-gray-400 mt-1">
                  all time
                </p>

              </div>


              {/* Latest Empathy */}
              <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">

                <p className="text-xs font-medium text-gray-500">
                  Latest empathy
                </p>

                <p className="text-2xl font-bold text-green-600 mt-1">
                  {latestEmpathy}
                </p>

                <p className="text-[11px] text-gray-400 mt-1">
                  last session
                </p>

              </div>


              {/* Trend */}
              <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">

                <p className="text-xs font-medium text-gray-500">
                  Trend
                </p>

                <div className="flex items-center gap-2 mt-1">

                  <span
                    className={`
                text-2xl
                font-bold
                ${trend.arrow === "↑"
                        ? "text-green-600"
                        : trend.arrow === "↓"
                          ? "text-red-500"
                          : "text-gray-500"
                      }
              `}
                  >
                    {trend.arrow}
                  </span>

                </div>

                <p className="text-[11px] text-gray-400 mt-1">
                  {trend.text}
                </p>

              </div>

            </div>


            {/* =================================================
          EMPATHY CHART
      ================================================== */}
            <section className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden mb-5">

              <div className="px-5 pt-5">

                <h2 className="text-sm font-bold text-gray-800">
                  Your empathy over time
                </h2>

                <p className="text-[11px] text-gray-500 mt-1">
                  Score per session, oldest to newest. No averaging — you see
                  the actual shape.
                </p>

              </div>

              {empathyData.length > 0 ? (

                <div className="px-5 pt-5 pb-6">

                  <div className="h-48 flex items-end gap-2 sm:gap-4">

                    {empathyData.map((item, index) => {

                      const maxScore = 10;

                      const height = `${Math.max(
                        (item.score / maxScore) * 100,
                        5
                      )}%`;

                      const isFirstTwo = index < 2;
                      const isLatest =
                        index === empathyData.length - 1;

                      return (
                        <div
                          key={`${item.session}-${index}`}
                          className="
                      flex-1
                      h-full
                      flex
                      flex-col
                      justify-end
                      items-center
                    "
                        >

                          <span className="text-[10px] text-gray-400 mb-1">
                            {item.score.toFixed(1)}
                          </span>

                          <div
                            className={`
                        w-full
                        max-w-[55px]
                        rounded-t-md
                        transition-all
                        duration-300
                        ${isLatest
                                ? "bg-green-400"
                                : isFirstTwo
                                  ? "bg-[#F7941D]"
                                  : "bg-[#aaa4e3]"
                              }
                      `}
                            style={{
                              height,
                            }}
                          />

                          <span className="text-[10px] text-gray-400 mt-2">
                            {item.session}
                          </span>

                        </div>
                      );
                    })}

                  </div>

                </div>

              ) : (

                <div className="px-5 py-12 text-center">

                  <p className="text-sm text-gray-500">
                    No empathy score data available.
                  </p>

                </div>

              )}

            </section>


            {/* =================================================
          SESSION HISTORY
      ================================================== */}
            <section className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden mb-5">

              <div className="px-5 py-4 border-b border-gray-100">

                <h2 className="text-sm font-bold text-gray-800">
                  Session history
                </h2>

              </div>


              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">

                <div className="min-w-[650px]">

                  <div
                    className="
                grid
                grid-cols-[1.1fr_2fr_1fr_0.8fr]
                px-5
                py-3
                bg-gray-50
                border-b
                border-gray-100
                text-[11px]
                font-semibold
                text-gray-500
              "
                  >
                    <div>Date</div>
                    <div>Case</div>
                    <div>Duration</div>
                    <div>Empathy</div>
                  </div>


                  {sortedHistory.length > 0 ? (

                    sortedHistory.map((session, index) => (

                      <div
                        key={session.id || index}
                        className="
                    grid
                    grid-cols-[1.1fr_2fr_1fr_0.8fr]
                    items-center
                    px-5
                    py-3
                    border-b
                    border-gray-100
                    last:border-b-0
                    text-xs
                    text-gray-600
                    hover:bg-gray-50
                    transition
                  "
                      >

                        <div>
                          {session.date}
                        </div>

                        <div className="font-medium text-gray-700">
                          {session.case}
                        </div>

                        <div>
                          {session.duration}
                        </div>

                        <div
                          className={`
                      font-bold
                      ${Number(session.empathy) >= 8
                              ? "text-green-600"
                              : "text-gray-800"
                            }
                    `}
                        >
                          {session.empathy !== null
                            ? Number(session.empathy).toFixed(1)
                            : "-"}
                        </div>

                      </div>

                    ))

                  ) : (

                    <div className="px-5 py-10 text-center text-sm text-gray-500">
                      No consultation history found.
                    </div>

                  )}

                </div>

              </div>


              {/* Mobile */}
              <div className="md:hidden">

                {sortedHistory.length > 0 ? (

                  sortedHistory.map((session, index) => (

                    <div
                      key={session.id || index}
                      className="
                  px-5
                  py-4
                  border-b
                  border-gray-100
                  last:border-b-0
                "
                    >

                      <div className="flex justify-between gap-4">

                        <div>

                          <p className="text-xs font-semibold text-gray-800">
                            {session.case}
                          </p>

                          <p className="text-[11px] text-gray-400 mt-1">
                            {session.date}
                          </p>

                        </div>

                        <span
                          className={`
                      text-sm
                      font-bold
                      ${Number(session.empathy) >= 8
                              ? "text-green-600"
                              : "text-gray-800"
                            }
                    `}
                        >
                          {session.empathy !== null
                            ? Number(session.empathy).toFixed(1)
                            : "-"}
                        </span>

                      </div>

                      <p className="text-[11px] text-gray-500 mt-2">
                        Duration: {session.duration}
                      </p>

                    </div>

                  ))

                ) : (

                  <div className="px-5 py-10 text-center text-sm text-gray-500">
                    No consultation history found.
                  </div>

                )}

              </div>

            </section>


            {/* =================================================
          CASES TO CONTINUE
      ================================================== */}
            <section className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">

              <div className="px-5 pt-5 pb-3">

                <h2 className="text-sm font-bold text-gray-800">
                  Cases to continue
                </h2>

                <p className="text-[11px] text-gray-500 mt-1">
                  Cases you’ve completed and the ones still open.
                </p>

              </div>


              <div className="px-5 pb-3">

                {[
                  ...sortedHistory.slice(0, 3).map((item) => ({
                    name: item.case,
                    status: "done",
                  })),
                  {
                    name: "Take more cases",
                    status: "start",
                  },
                  // {
                  //   name: "Lifestyle counseling",
                  //   status: "start",
                  // },
                ].map((item, index) => {

                  const completed = item.status === "done";

                  return (
                    <div
                      key={`${item.name}-${index}`}
                      className="
                  flex
                  items-center
                  justify-between
                  gap-4
                  py-3
                  border-b
                  border-gray-100
                  last:border-b-0
                "
                    >

                      <div className="flex items-center gap-3">

                        <span
                          className={`
                      w-2
                      h-2
                      rounded-full
                      flex-shrink-0
                      ${completed
                              ? "bg-green-500"
                              : "bg-purple-400"
                            }
                    `}
                        />

                        <span
                          className={`
                      text-xs
                      ${completed
                              ? "text-gray-400"
                              : "text-gray-700 font-medium"
                            }
                    `}
                        >
                          {item.name}
                        </span>

                      </div>


                      {completed ? (

                        <span className="text-[11px] text-gray-400">
                          done
                        </span>

                      ) : (

                        <button
                          type="button"
                          onClick={() => handleStartCase(item)}
                          className="
                      bg-[#1D3A73]
                      hover:bg-[#162d5a]
                      text-white
                      text-[11px]
                      font-semibold
                      px-4
                      py-1.5
                      rounded-full
                      transition
                      duration-200
                    "
                        >
                          Start
                        </button>

                      )}

                    </div>
                  );
                })}

              </div>

            </section>

          </div>

        </div>

      </main>

      {/* =====================================================
          FOOTER
      ====================================================== */}
      <FooterSection />

    </div>
  );
};

export default ReportDashboard;